import { useCallback, useEffect, useState } from "react";
import { fetchBoard, IS_DEV_DB, moveAssignment, setViewAs, solveScenario, type Board } from "./api.js";
import { HoldSolve, readHold, writeHold } from "./components/HoldSolve.js";
import { GanttByResource, GanttByProject, ZOOMS, type Zoom } from "./components/Gantt.js";
import { ScenarioBar } from "./components/ScenarioBar.js";
import { Backlog, Capacity, Dashboard } from "./components/Views.js";
import { Ask } from "./components/Ask.js";
import { DslAdmin } from "./components/Admin.js";
import { People } from "./components/People.js";
import { Breakdown } from "./components/Breakdown.js";
import { Skills } from "./components/Skills.js";
import { Templates } from "./components/Templates.js";
import { Import } from "./components/Import.js";
import { Compare } from "./components/Compare.js";
import { Today } from "./components/Today.js";
import { Work } from "./components/Work.js";
import { Rules } from "./components/Rules.js";
import { MyWork } from "./components/MyWork.js";
import { FilterBar, applyFilters, identityFiltered, useBoardFilter } from "./components/Filters.js";
import { SessionChip, SignInGate } from "./components/SignIn.js";
import { NewScenario } from "./components/NewScenario.js";
import { RunLog } from "./components/RunLog.js";
import { Requirements } from "./components/Requirements.js";
import { Feedback } from "./components/Feedback.js";
import { authConfigured, onSessionChange, takeReturnTo } from "./session.js";
import type { Priority } from "../domain/types.js";

type View =
  | "today" | "dashboard" | "resource" | "project" | "backlog" | "capacity"
  | "projects" | "templates" | "skills" | "import" | "compare" | "resources" | "work"
  | "dsl" | "rules" | "runs" | "mywork" | "feedback";

/** The views a scenario means anything on. Elsewhere the bar is just noise. */
const PLANNING: View[] = ["dashboard", "resource", "project", "backlog", "capacity"];

/**
 * The "Manage" section — roster, constraint vocabulary, how the solver's
 * rules work, and its run log. The client asked that Engineers not see any of
 * it, not just People: it's the shop's configuration, not their own work.
 */
/** What a screen is called on the Back button of a project opened from it. */
const VIEW_LABEL: Partial<Record<View, string>> = {
  mywork: "My work", backlog: "the Backlog", resource: "the Schedule", project: "Delivery",
  dashboard: "the Dashboard", today: "Today",
};

const MANAGE: View[] = ["resources", "dsl", "rules", "runs"];

const VIEWS: View[] = [
  "today", "dashboard", "resource", "project", "backlog", "capacity",
  "projects", "templates", "skills", "import", "compare", "resources", "work",
  "dsl", "rules", "runs", "mywork", "feedback",
];

export function App() {
  const [board, setBoard] = useState<Board | null>(null);
  // No session means the gate and nothing else: every call the board would
  // make is refused with a 401, so rendering it would look broken, not locked.
  const [signedIn, setSignedIn] = useState<boolean | null>(authConfigured ? null : true);
  useEffect(() => onSessionChange((u) => setSignedIn(u !== null)), []);

  // Views are addressable — ?view=resource deep-links, and the hash keeps the
  // current board shareable with someone you want to look at the same thing.
  //
  // Computed once, in the initializer: `takeReturnTo()` consumes the stashed
  // value, so reading it on every render would return the deep link on the
  // first render and null on every one after — which would let a later
  // render override it back to the default view.
  // Computed once, alongside `view` below: whether the visitor asked for a
  // specific view (a deep link or a returning hash) or landed with nothing,
  // which is the only case the Engineer landing effect further down is
  // allowed to override.
  const [landing] = useState<{ view: View; explicit: boolean }>(() => {
    // A deep link that went through the provider comes back at the bare
    // origin, so the destination is read from the stash first.
    const returned = takeReturnTo();
    const from = returned ?? location.search + location.hash;
    const [qs, hash] = from.split("#");
    const requested = (new URLSearchParams(qs ?? "").get("view") ?? hash ?? "") as View;
    const explicit = VIEWS.includes(requested);
    // Today is the landing view: the daily path is one screen, and everything
    // else stays exactly where it was for the day that needs it.
    return { view: explicit ? requested : "today", explicit };
  });
  const [view, setViewRaw] = useState<View>(landing.view);
  const setView = (v: View) => {
    setViewRaw(v);
    history.replaceState(null, "", `#${v}`);
  };
  const openProject = (id: string) => {
    setProjectFrom(view === "projects" ? null : view);
    setProjectFocus(id);
    setView("projects");
  };
  // A way back only makes sense while on the Projects screen it was opened for.
  useEffect(() => { if (view !== "projects") setProjectFrom(null); }, [view]);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [scenarioId, setScenarioId] = useState<string | undefined>();
  const [askOpen, setAskOpen] = useState(false);
  const [newOpen, setNewOpen] = useState(false);
  // Bumped to make the scenario bar open its change summary.
  const [autoDiff, setAutoDiff] = useState(0);
  // Bumped when a requirement is added from somewhere other than the list.
  const [reqToken, setReqToken] = useState(0);
  const [zoom, setZoom] = useState<Zoom>(8);
  const { filter, setFilter } = useBoardFilter();
  // A project to open when the Projects screen next shows, set by double-clicking a bar on the schedule.
  const [projectFocus, setProjectFocus] = useState<string | null>(null);
  // The screen a project was opened from, so Back returns there and not to the project list.
  const [projectFrom, setProjectFrom] = useState<View | null>(null);
  // A drag now re-solves, which takes seconds. The board must not look live.
  const [solving, setSolving] = useState(false);
  // Hold the re-solve while making many moves: the moves are saved as they happen,
  // and "Solve now" applies them together. Kept per browser, not per draft.
  // The Backlog's "over estimate only" view, switched on by the Dashboard's Fix button.
  const [overOnly, setOverOnly] = useState(false);
  const [hold, setHold] = useState<boolean>(() => readHold());
  const [heldMoves, setHeldMoves] = useState(0);

  // Cmd-K from anywhere. Ask is not a nav destination.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setAskOpen((v) => !v);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const load = useCallback(async (sid?: string) => {
    try {
      const b = await fetchBoard(sid);
      setBoard(b);
      setScenarioId(b.scenarioId);
      setError(null);
    } catch (e) {
      setError((e as Error).message);
    }
  }, []);

  // Gated on signedIn, not on mount: every render runs this hook regardless of
  // which branch the component returns, so an unconditional `[]` effect would
  // fire fetchBoard() — and get a 401 back — while the gate is still on
  // screen for an anonymous visitor. `signedIn` starts true outright when the
  // build has no Supabase config, so local dev still loads on mount.
  useEffect(() => {
    if (!signedIn) return;
    void load(scenarioId);
  }, [signedIn]);

  // An Engineer lands on their own work; Today is scheduler-shaped — it leads
  // with what needs a decision across the shop, which is not an engineer's
  // question. The role is only known once the board loads, so this is an
  // effect, not part of the initial-view derivation above. Fires once, and
  // never over a view the visitor explicitly asked for (`landing.explicit`).
  const [landed, setLanded] = useState(false);
  useEffect(() => {
    if (landed || !board) return;
    setLanded(true);
    if (board.user.role === "engineer" && MANAGE.includes(landing.view)) {
      // Off-limits regardless of how they got there — a deep link or a
      // returning hash is not consent from the client to show it anyway.
      setView("mywork");
      return;
    }
    if (!landing.explicit && board.user.role === "engineer") setView("mywork");
  }, [board, landed, landing]);

  const onMove = useCallback(
    async (
      id: string, patch: { resourceId?: string | null; queuePosition?: number; priority?: Priority },
    ): Promise<{ ok: boolean; error?: string }> => {
      // A reassignment re-solves the whole scenario server-side, so the board
      // is stale for as long as that takes. Say so rather than leaving a board
      // that still shows the old answer and accepts more drags onto it.
      const held = hold && patch.resourceId !== undefined;
      if (patch.resourceId !== undefined && !held) setSolving(true);
      try {
        const r = await moveAssignment(id, { ...patch, scenarioId, ...(held ? { hold: true } : {}) });
        await load(scenarioId);
        if (r.held) {
          setReqToken((n) => n + 1);
          setHeldMoves((n) => n + 1);
          setToast("Saved. Re-solving is on hold, so nothing else has moved yet.");
          setTimeout(() => setToast(null), 6000);
          return { ok: true };
        }
        // The move joined the requirement list, so the list has to show it —
        // otherwise the row that now governs the schedule is invisible until
        // the next reload, which is the whole failure this replaced.
        if (r.requirements) {
          setReqToken((n) => n + 1);
          setToast(`Recorded as a requirement — re-solved, ${r.churn ?? 0} phases changed hands.`);
          setTimeout(() => setToast(null), 6000);
        }
        return { ok: true };
      } catch (e) {
        // A refusal is information, not a failure — show the rule that fired.
        // Also returned to the caller so a list-view row can show it inline,
        // rather than only in a toast that clears itself in 6 seconds.
        const message = (e as Error).message;
        setToast(message);
        setTimeout(() => setToast(null), 6000);
        return { ok: false, error: message };
      } finally {
        setSolving(false);
      }
    },
    [load, scenarioId, hold],
  );

  useEffect(() => { setHeldMoves(0); }, [scenarioId]);

  const solveNow = useCallback(async () => {
    if (!scenarioId) return;
    setSolving(true);
    try {
      const r = await solveScenario(scenarioId);
      await load(scenarioId);
      setReqToken((n) => n + 1);
      setHeldMoves(0);
      setAutoDiff((n) => n + 1);
      setToast(`Solved — ${r.churn ?? 0} phases changed hands.`);
    } catch (e) {
      setToast((e as Error).message);
    } finally {
      setSolving(false);
      setTimeout(() => setToast(null), 6000);
    }
  }, [load, scenarioId]);


  // Dev and prod share one database (see DEPLOY.md) — this is not a staging
  // warning, it's a statement of fact: every write made here lands in the
  // same rows prod reads. Shown on every branch below, including sign-in,
  // since the risk starts the moment someone lands on a dev build, not once
  // they're past the gate.
  const devBanner = IS_DEV_DB && (
    <div className="dev-db-banner">Editing Production Database</div>
  );

  if (signedIn === null) {
    return (
      <>
        {devBanner}
        <div className="gate"><p className="muted">Checking your session…</p></div>
      </>
    );
  }
  if (!signedIn) return <>{devBanner}<SignInGate /></>;

  // Undecided until the board loads — same as every other nav item, which
  // renders before that too. The landing effect above is what actually keeps
  // an Engineer out; this just keeps the door from being drawn on the wall.
  const canManage = board?.user.role !== "engineer";

  return (
    <div className={`app${IS_DEV_DB ? " with-dev-db-banner" : ""}`}>
      {devBanner}
      <nav className="nav">
        <h1>Engineering<br />Scheduling</h1>
        <button className={view === "today" ? "on" : ""} onClick={() => setView("today")}>Today</button>
        <button className={view === "mywork" ? "on" : ""} onClick={() => setView("mywork")}>My work</button>
        <div className="nav-grp">Plan</div>
        <button className={view === "dashboard" ? "on" : ""} onClick={() => setView("dashboard")}>Dashboard</button>
        <button className={view === "resource" ? "on" : ""} onClick={() => setView("resource")}>
          Schedule <span className="badge">{board?.resources.length ?? "—"}</span>
        </button>
        <button className={view === "project" ? "on" : ""} onClick={() => setView("project")}>
          Delivery <span className="badge">{board?.projects.length ?? "—"}</span>
        </button>
        <button className={view === "backlog" ? "on" : ""} onClick={() => setView("backlog")}>
          Backlog <span className="badge">{board ? board.phases.filter((p) => p.remainingHours > 0).length : "—"}</span>
        </button>
        <div className="nav-grp">Measure</div>
        <button className={view === "capacity" ? "on" : ""} onClick={() => setView("capacity")}>Capacity</button>
        <div className="nav-grp">Define</div>
        <button className={view === "projects" ? "on" : ""} onClick={() => setView("projects")}>
          Projects <span className="badge">{board?.projects.length ?? "—"}</span>
        </button>
        <button className={view === "templates" ? "on" : ""} onClick={() => setView("templates")}>Templates</button>
        <button className={view === "skills" ? "on" : ""} onClick={() => setView("skills")}>Skills</button>
        <button className={view === "work" ? "on" : ""} onClick={() => setView("work")}>Work types</button>
        <div className="nav-grp">Data</div>
        <button className={view === "import" ? "on" : ""} onClick={() => setView("import")}>Daily file</button>
        <button className={view === "compare" ? "on" : ""} onClick={() => setView("compare")}>Compare plans</button>
        {canManage && (
          <>
            <div className="nav-grp">Manage</div>
            <button className={view === "resources" ? "on" : ""} onClick={() => setView("resources")}>
              People <span className="badge">{board?.resources.filter((r) => !r.isPool).length ?? "—"}</span>
            </button>
            <button className={view === "dsl" ? "on" : ""} onClick={() => setView("dsl")}>Vocabulary</button>
            <button className={view === "rules" ? "on" : ""} onClick={() => setView("rules")}>How rules work</button>
            <button className={view === "runs" ? "on" : ""} onClick={() => setView("runs")}>Optimizer log</button>
          </>
        )}
        <div className="nav-grp">Reference</div>
        <a className="nav-link" href="/spec.html" target="_blank" rel="noopener">
          Specification <span className="badge">↗</span>
        </a>
        <button className={view === "feedback" ? "on" : ""} onClick={() => setView("feedback")}>Feedback</button>
      </nav>

      <div className="main">
        <div className="topbar">
          <span className="title">
            {view === "today" ? "Today"
              : view === "mywork" ? "My work"
              : view === "projects" ? "Projects · what Epicor sends, and what you define"
              : view === "templates" ? "Templates · the breakdown a new project arrives with"
              : view === "skills" ? "Skills · who can do what"
              : view === "work" ? "Work types and states · the words for work"
              : view === "rules" ? "How the rules work"
              : view === "import" ? "Daily file · projects and their due dates"
              : view === "compare" ? "Compare plans · the file against the optimizer"
              : view === "resource" ? "Schedule · by resource"
              : view === "project" ? "Schedule · by project"
              : view === "backlog" ? "Backlog"
              : view === "capacity" ? "Capacity"
              : view === "resources" ? "People · capacity and time off"
              : view === "dsl" ? "Admin · constraint vocabulary"
              : view === "runs" ? "Optimizer log · what the solver was given and returned"
              : view === "feedback" ? "Feedback"
              : "Dashboard"}
          </span>
          {(view === "resource" || view === "project") && (
            <span className="zoom">
              <span className="lab">weeks</span>
              {ZOOMS.map((z) => (
                <button key={z} className={`btn${zoom === z ? " on" : ""}`} onClick={() => setZoom(z)}>{z}</button>
              ))}
            </span>
          )}
          <span className="spacer" />
          {board?.user.viewingAs && (
            <span className="chip draft" style={{ marginRight: 8 }}>
              Viewing as {board.user.viewingAs.name}
              <button
                className="btn" style={{ marginLeft: 8 }}
                onClick={() => { setViewAs(null); void load(scenarioId); }}
              >
                Exit
              </button>
            </span>
          )}
          <SessionChip role={board?.user.role} onChange={() => void load(scenarioId)} />

          <button className="btn" onClick={() => void load(scenarioId)}>Refresh</button>
          <button className="btn on" onClick={() => setAskOpen(true)}>Ask ⌘K</button>
        </div>

        {board && PLANNING.includes(view) && (
          <ScenarioBar
            board={board}
            onSwitch={(id) => { setScenarioId(id); void load(id); }}
            onCreate={() => setNewOpen(true)}
            autoDiff={autoDiff}
            onPublished={() => void load(board.scenarioId)}
          />
        )}
        {board && PLANNING.includes(view) && board.scenarios.find((s) => s.scenarioId === board.scenarioId)?.status === "draft"
          && (board.user.role === "admin" || board.user.role === "manager") && (
          <HoldSolve
            hold={hold}
            waiting={heldMoves}
            solving={solving}
            onToggle={(v) => { setHold(v); writeHold(v); }}
            onSolve={() => void solveNow()}
          />
        )}
        {board && ["resource", "project", "backlog", "dashboard"].includes(view) && (
          <Requirements
            scenarioId={board.scenarioId}
            role={board.user.role}
            refreshToken={reqToken}
            onSolved={(note) => {
              void load(board.scenarioId).then(() => setAutoDiff((n) => n + 1));
              if (note) { setToast(note); setTimeout(() => setToast(null), 6000); }
            }}
          />
        )}

        <div className={`body${solving ? " solving" : ""}`}>
          {solving && <div className="solving-note">Re-solving the scenario…</div>}
          {error && (
            <div className="err">
              <b>Could not load the board.</b>
              <div className="mono" style={{ marginTop: 6, fontSize: 12 }}>{error}</div>
            </div>
          )}
          {!board && !error && <p className="muted">Loading…</p>}
          {view === "today" && (
            <Today
              // Today's own "away" notice links to People — off-limits to an
              // Engineer same as the nav button it stands in for.
              go={(v) => setView(!canManage && MANAGE.includes(v as View) ? "mywork" : (v as View))}
              openScenario={(id) => { setScenarioId(id); void load(id); setView("resource"); }}
              fixOverEstimate={() => { setOverOnly(true); setView(canManage ? "backlog" : "mywork"); }}
            />
          )}
          {board && view === "mywork" && <MyWork board={board} reload={() => void load(scenarioId)} onOpenProject={openProject} />}
          {view === "projects" && (
            <Breakdown onChanged={() => void load(scenarioId)}
              focusProjectId={projectFocus} onFocused={() => setProjectFocus(null)}
              board={board} onMove={onMove}
              backTo={projectFrom ? { label: VIEW_LABEL[projectFrom] ?? "the last screen", go: () => setView(projectFrom) } : null} />
          )}
          {view === "templates" && <Templates />}
          {view === "skills" && <Skills />}
          {view === "work" && <Work />}
          {canManage && view === "rules" && <Rules go={(v) => setView(v as View)} />}
          {view === "import" && (
            <Import role={board?.user.role}
              onScenario={(id) => { setScenarioId(id); void load(id); setView("resource"); }} />
          )}
          {board && view === "compare" && <Compare board={board} />}
          {board && view === "dashboard" && <Dashboard board={board} go={(v) => setView(v as View)}
            fixOverEstimate={() => { setOverOnly(true); setView("backlog"); }} />}
          {board && view === "resource" && (
            <>
              <FilterBar board={board} filter={filter} onChange={setFilter}
                show={["person", "team", "project", "customer"]} mineResourceId={board.user.resourceId} />
              <GanttByResource
                board={applyFilters(board, filter)} zoom={zoom} onMove={onMove}
                keepIdleResources={identityFiltered(filter)}
                onOpenProject={openProject}
              />
            </>
          )}
          {board && view === "project" && (
            <>
              <FilterBar board={board} filter={filter} onChange={setFilter}
                show={["project", "customer", "person", "team"]} mineResourceId={board.user.resourceId} />
              <GanttByProject board={applyFilters(board, filter)} zoom={zoom}
                onOpenProject={openProject} />
            </>
          )}
          {board && view === "backlog" && <Backlog board={board} onMove={onMove} overOnly={overOnly} onOverOnly={setOverOnly}
            onOpenProject={openProject} />}
          {board && view === "capacity" && <Capacity board={board} />}
          {canManage && board && view === "resources" && (
            <People
              board={board}
              reload={() => void load(scenarioId)}
              onViewAs={(resourceId) => {
                setViewAs(resourceId);
                void load(scenarioId).then(() => setView("mywork"));
              }}
            />
          )}
          {canManage && view === "dsl" && (
            <DslAdmin reload={() => void load(scenarioId)} go={(v) => setView(v as View)} />
          )}
          {canManage && view === "runs" && <RunLog />}
          {board && view === "feedback" && <Feedback board={board} />}
        </div>
      </div>
      <NewScenario
        open={newOpen}
        onClose={() => setNewOpen(false)}
        role={board?.user.role}
        onCreated={async (id, note) => {
          setScenarioId(id);
          await load(id);
          setAutoDiff((n) => n + 1);
          if (note) { setToast(note); setTimeout(() => setToast(null), 9000); }
        }}
      />

      <Ask
        open={askOpen}
        onClose={() => setAskOpen(false)}
        scenarioId={board?.scenarioId}
        context={`on ${view}`}
        onPublished={(sid) => {
          setScenarioId(sid);
          void load(sid).then(() => setAutoDiff((n) => n + 1));
          // The requirement list is the thing that just changed, so refresh it
          // in place. Ask stays open — its explanation is worth reading, and
          // closing the panel to reveal the result was the awkward part.
          setReqToken((n) => n + 1);
        }}
      />
      {toast && <div className="toast">{toast}</div>}
    </div>
  );
}
